import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { Check, ChevronRight, Copy, Grid2X2, House, Lightbulb, Menu, Share, Smartphone, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getInstallBrowser } from './installBrowser';
import { INSTALL_PLATFORMS, type InstallPlatform } from './installPlatforms';
import { requestInstall, useInstallState, type InstallResult } from './installPrompt';
import './install-guide.css';

const stepIcons = { share: Share, menu: Menu, grid: Grid2X2, home: House, check: Check };
const resultMessage = (outcome: InstallResult) => outcome === 'accepted' ? '已提交安装，请按浏览器提示完成。'
  : outcome === 'unconfirmed' ? '浏览器尚未返回安装结果。如果确认框已打开，请继续确认；没有弹出时，可按下面的步骤手动添加。'
    : outcome === 'dismissed' ? '已取消安装，也可以按下面的步骤手动添加。'
      : '暂时无法调起安装，请按下面的步骤手动添加。';

export default function InstallGuideSheet({ onClose, trigger, installation }: {
  onClose: () => void; trigger: Element | null; installation?: Promise<InstallResult>;
}) {
  const { available, busy, installed } = useInstallState();
  const browser = getInstallBrowser(navigator.userAgent);
  const [platform, setPlatform] = useState<InstallPlatform>(() =>
    /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1 ? 'ios' : browser.platform);
  const [message, setMessage] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [copyFailed, setCopyFailed] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const urlRef = useRef<HTMLInputElement>(null);
  const applyResult = useCallback((outcome: InstallResult) => {
    setMessage(resultMessage(outcome));
    setAccepted(outcome === 'accepted');
  }, []);
  useEffect(() => {
    let current = true;
    if (installation) void installation.then(outcome => { if (current) applyResult(outcome); });
    return () => { current = false; };
  }, [installation, applyResult]);
  useEffect(() => {
    if (available && !accepted) setMessage('');
  }, [available, accepted]);
  useEffect(() => {
    if (copyFailed) { urlRef.current?.focus(); urlRef.current?.select(); }
  }, [copyFailed]);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + '/');
      setCopyFailed(false);
      setCopyMessage('链接已复制，请粘贴到浏览器打开');
    } catch {
      setCopyFailed(true);
      setCopyMessage('请长按下方网址复制，再到手机浏览器打开');
    }
  };
  const guide = INSTALL_PLATFORMS[platform];
  return <Dialog.Root open onOpenChange={value => { if (!value) onClose(); }}>
    <Dialog.Portal>
      <Dialog.Overlay className="install-guide-overlay" />
      <Dialog.Content className="install-guide-sheet" onOpenAutoFocus={event => {
        event.preventDefault(); titleRef.current?.focus();
      }} onCloseAutoFocus={event => {
        event.preventDefault();
        if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus({ preventScroll: true });
      }}>
        <div className="install-guide-scroll">
          <header className="install-guide-header">
            <div className="install-guide-heading">
              <Dialog.Title ref={titleRef} tabIndex={-1}>添加到桌面</Dialog.Title>
              <Dialog.Close asChild><button className="install-guide-close" type="button" aria-label="关闭添加桌面引导"><X /></button></Dialog.Close>
            </div>
            <Dialog.Description className="install-guide-hint"><Lightbulb aria-hidden="true" />
              <span>{browser.embedded ? '请先复制链接，到系统浏览器打开' : guide.hint}</span>
            </Dialog.Description>
          </header>
          <Tabs.Root value={platform} onValueChange={value => setPlatform(value as InstallPlatform)}>
            <Tabs.List className="install-guide-tabs" aria-label="设备类型">
              {(Object.keys(INSTALL_PLATFORMS) as InstallPlatform[]).map(id =>
                <Tabs.Trigger key={id} value={id}>{INSTALL_PLATFORMS[id].label}</Tabs.Trigger>)}
            </Tabs.List>
            {(Object.keys(INSTALL_PLATFORMS) as InstallPlatform[]).map(id => <Tabs.Content key={id} value={id} className="install-guide-panel">
              <ol className="install-guide-steps">{INSTALL_PLATFORMS[id].steps.map((step, index) => {
                const Icon = stepIcons[step.icon];
                return <li key={step.title}>
                  <span className="install-guide-step-icon"><Icon aria-hidden="true" /><span className="install-guide-number">{index + 1}</span></span>
                  <div className="install-guide-step-copy"><h3>{step.title}</h3><p>{step.text}</p></div>
                  {index < 2 && <ChevronRight className="install-guide-chevron" aria-hidden="true" />}
                </li>;
              })}</ol>
              <aside className="install-guide-tip"><Lightbulb aria-hidden="true" /><p>{INSTALL_PLATFORMS[id].tip}</p></aside>
            </Tabs.Content>)}
          </Tabs.Root>
          {(installed || available || busy || accepted) && <div className="install-guide-ready" role="status">
            {installed ? <Check aria-hidden="true" /> : <Smartphone aria-hidden="true" />}
            <div><strong>{installed ? '已安装或正在独立应用中使用练刻' : accepted ? '安装请求已提交' : busy ? '正在请求浏览器添加' : '此浏览器支持直接添加'}</strong>
              <p>{installed ? '下次可从桌面图标打开。' : accepted ? '请等待浏览器完成安装。' : busy ? '如出现安装确认框，请确认；也可以随时关闭此引导。' : '可点击下方直接添加按钮，在浏览器中确认。'}</p></div>
          </div>}
          {message && !installed && <p className="install-guide-message" role="status">{message}</p>}
          {(available || busy) && !installed && !accepted && <button type="button" className="install-guide-primary install-guide-native" disabled={busy}
            onClick={() => { void requestInstall().then(applyResult); }}>
            <Smartphone aria-hidden="true" />{busy ? '正在打开安装确认…' : '直接添加到桌面'}
          </button>}
          <button type="button" className="install-guide-primary install-guide-copy" onClick={() => { void copyLink(); }}>
            <Copy aria-hidden="true" />复制链接，到浏览器打开
          </button>
          {copyFailed && <input ref={urlRef} className="install-guide-url" aria-label="网站链接" readOnly value={window.location.origin + '/'} onFocus={event => event.currentTarget.select()} />}
          <p className="install-guide-caption" role="status">{copyMessage || '添加后，下次从桌面图标进入练刻'}</p>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>;
}
